import React, {useEffect, useState} from 'react';
import {getMessage} from '../utils/messages';
import {TaskPicker} from './TaskPicker';
import type {Config, ProjectTimeRecord, TaskOption} from '../types/types';
import {formatDuration} from "@/shared/utils/datefunctions";

export type MonthlyEntryFormValues = {
	comment: string;
	durationMs: number;
	task: TaskOption;
};

type MonthlyEntryFormProps = {
	config: Config;
	maxDurationMs: number;
	record?: ProjectTimeRecord;
	canEditDuration: boolean;
	disabled?: boolean;
	formId: string;
	onSubmit: (values: MonthlyEntryFormValues) => Promise<void>;
};

export const MonthlyProjectTimeForm: React.FC<MonthlyEntryFormProps> = ({
																			config,
																			maxDurationMs,
																			record,
																			canEditDuration,
																			disabled,
																			formId,
																			onSubmit,
																		}) => {
	const [selectedTask, setSelectedTask] = useState<TaskOption | undefined>(() => record?.task);
	const [durationInput, setDurationInput] = useState<string>(() => record?.durationNet ? formatDuration(record.durationNet) : '');
	const [comment, setComment] = useState<string>(() => record?.description ?? '');
	const [durationError, setDurationError] = useState<string>('');
	const [taskError, setTaskError] = useState<string>('');
	const isDisabled = Boolean(disabled);

	useEffect(() => {
		if (record) {
			setSelectedTask(record.task);
			setDurationInput(record.durationNet ? formatDuration(record.durationNet) : '');
			setComment(record.description ?? '');
		} else {
			setSelectedTask(undefined);
			setDurationInput('');
			setComment('');
		}
		setDurationError('');
		setTaskError('');
	}, [record]);

	const parseDurationInput = (value: string): number | null => {
		const trimmed = value.trim();
		if (!trimmed) {
			return null;
		}
		if (trimmed.includes(':')) {
			const [hoursText, minutesText = '0'] = trimmed.split(':');
			const hours = Number(hoursText);
			const minutes = Number(minutesText);
			return Number.isNaN(hours) || Number.isNaN(minutes) ? null : (hours * 60 + minutes) * 60000;
		}
		const hoursDecimal = Number(trimmed.replace(',', '.'));
		return Number.isNaN(hoursDecimal) ? null : Math.round(hoursDecimal * 3600000);
	};

	const handleSubmit = async () => {
		const durationMs = parseDurationInput(durationInput);
		if (!selectedTask) {
			setTaskError(getMessage('monthlyRecording.form.error.selectTask'));
			return;
		}
		if (!durationMs) {
			setDurationError(getMessage('monthlyRecording.form.error.enterDuration'));
			return;
		}
		if (durationMs > maxDurationMs) {
			setDurationError(getMessage('monthlyRecording.error.exceedsMonth'));
			return;
		}

		setTaskError('');
		setDurationError('');
		await onSubmit({task: selectedTask, comment, durationMs});
		if (!record) {
			setDurationInput('');
			setComment('');
		}
	};

	const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		void handleSubmit();
	};

	return (
		<form className="form-horizontal" onSubmit={handleFormSubmit} id={formId}>
			<div className="row">
				<div className="col-lg-8 col-xs-12">
					<div className="form-group">
						<label className="control-label col-xs-2">{getMessage('record.task')}</label>
						<div className="col-xs-10">
							<TaskPicker
								config={config}
								selectedTask={selectedTask}
								onSelect={task => {
									setSelectedTask(task);
									if (taskError) {
										setTaskError('');
									}
								}}
								disabled={isDisabled}
							/>
							{taskError && <div className="help-block text-danger">{taskError}</div>}
						</div>
					</div>
					<div className="form-group">
						<label className="control-label col-xs-2">{getMessage('record.bulksetnote.title')}</label>
						<div className="col-xs-10">
							<textarea
								className="form-control"
								rows={4}
								value={comment}
								onChange={event => setComment(event.target.value)}
								placeholder={getMessage('monthlyRecording.form.placeholder.noteOptional')}
								disabled={isDisabled}
							/>
						</div>
					</div>
				</div>
				<div className="col-lg-4 col-xs-12">
					<div className="form-group align-content-center">
						<label className="control-label col-lg-4 col-xs-2 text-nowrap">{getMessage('record.duration')}</label>
						<div className=" col-lg-8 col-xs-3 text-right" style={{width: '135px'}}>
							<input
								type="text"
								className="form-control"
								value={durationInput}
								onChange={event => {
									setDurationInput(event.target.value);
									if (durationError) {
										setDurationError('');
									}
								}}
								placeholder={getMessage('monthlyRecording.form.placeholder.duration')}
								disabled={isDisabled || !canEditDuration}
							/>
							{durationError && <div className="help-block text-danger">{durationError}</div>}
							{!canEditDuration && (
								<div className="help-block text-muted">
									{getMessage('monthlyRecording.form.durationLocked')}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</form>
	);
};
